import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ProgressMessages from "@/components/ProgressMessages";
import { 
  Radar, 
  Plus, 
  Trash2, 
  ExternalLink, 
  RefreshCw, 
  TrendingUp,
  Clock,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  AlertCircle,
  X,
  Globe,
  Instagram,
  Facebook,
  ArrowLeft,
  ArrowRight,
  FileText,
  Video,
  Image,
  Layers,
  Check,
  Sparkles,
  Loader2,
  Calendar
} from "lucide-react";

const platforms = [
  { id: "facebook", name: "Facebook", color: "#1877F2" },
  { id: "instagram", name: "Instagram", color: "#E4405F" },
  { id: "tiktok", name: "TikTok", color: "#000000" }
];

const tones = [
  { id: "professional", label: "Professional", icon: "briefcase" },
  { id: "casual", label: "Casual", icon: "smile" },
  { id: "humorous", label: "Humorous", icon: "laugh" },
  { id: "inspirational", label: "Inspirational", icon: "star" },
];

export default function ContentRadarPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [radarItems, setRadarItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  // Creation flow state
  const [creationStep, setCreationStep] = useState(0); // 0 = radar view, 1-5 = creation steps
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [scheduleType, setScheduleType] = useState(null);
  const [contentType, setContentType] = useState("post");
  const [selectedPlatforms, setSelectedPlatforms] = useState(["facebook", "instagram", "tiktok"]);
  const [selectedTone, setSelectedTone] = useState("professional");
  const [days, setDays] = useState(7);
  const [postsPerDay, setPostsPerDay] = useState(1);
  const [progress, setProgress] = useState(0);
  const [generatedPosts, setGeneratedPosts] = useState([]);

  // Plan limits
  const planLimits = {
    free_trial: 1,
    starter: 3,
    growth: 5,
    pro: 10
  };
  const maxUrls = planLimits[user?.plan] || 1;

  // Credits
  const postsRemaining = (user?.posts_remaining || 0) + (user?.payg_posts_remaining || 0);
  const videosRemaining = user?.video_credits_remaining || 0;
  const planValidityDays = user?.plan_validity_days || 90;
  const maxDays = Math.min(planValidityDays, 90);

  const isVideoContent = contentType === "video";
  // For both post and video: days * postsPerDay * platforms for bulk, or platforms for single
  const creditsNeeded = scheduleType === "bulk" 
    ? days * postsPerDay * selectedPlatforms.length
    : selectedPlatforms.length;
  const creditsAvailable = isVideoContent ? videosRemaining : postsRemaining;
  const hasEnoughCredits = creditsNeeded <= creditsAvailable;
  const creditType = isVideoContent ? "video" : "post";

  useEffect(() => {
    fetchRadarItems();
  }, []);

  const fetchRadarItems = async () => {
    try {
      const response = await apiClient.get("/content/radar");
      setRadarItems(response.data || []);
    } catch (error) {
      // Mock data for demo
      setRadarItems([
        {
          id: "1",
          name: "TechCrunch",
          url: "https://techcrunch.com",
          platform: "website",
          last_checked: new Date().toISOString(),
          notifications_enabled: false,
          metrics: { posts_today: 12, avg_engagement: 4500, trending_topics: ["AI", "Startups", "Funding"] },
          recent_posts: [
            { title: "OpenAI launches new model", engagement: 8500, time: "2h ago" },
            { title: "Startup raises $50M Series B", engagement: 3200, time: "4h ago" }
          ]
        },
        {
          id: "2", 
          name: "@garyvee",
          url: "https://instagram.com/garyvee",
          platform: "instagram",
          last_checked: new Date().toISOString(),
          notifications_enabled: true,
          metrics: { posts_today: 3, avg_engagement: 125000, trending_topics: ["Motivation", "Business", "NFTs"] },
          recent_posts: [
            { title: "Stop making excuses", engagement: 185000, time: "6h ago" },
            { title: "The patience game", engagement: 92000, time: "1d ago" }
          ]
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRadarItems();
    toast.success("Radar updated!");
    setRefreshing(false);
  };

  const handleAddCompetitor = async () => {
    if (!newUrl.trim() || !newName.trim()) {
      toast.error("Please enter both name and URL");
      return;
    }

    if (radarItems.length >= maxUrls) {
      toast.error(`Radar limit reached (${maxUrls} URLs). Upgrade to add more.`);
      return;
    }

    setAdding(true);
    try {
      const response = await apiClient.post("/content/radar", {
        url: newUrl,
        name: newName
      });
      
      const newItem = {
        id: response.data?.radar_id || Date.now().toString(),
        name: newName,
        url: newUrl,
        platform: detectPlatform(newUrl),
        last_checked: null,
        notifications_enabled: false,
        metrics: { posts_today: 0, avg_engagement: 0, trending_topics: [] },
        recent_posts: []
      };
      
      setRadarItems([...radarItems, newItem]);
      setNewUrl("");
      setNewName("");
      setShowAddModal(false);
      toast.success("Competitor added to radar!");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to add competitor";
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id) => {
    try {
      await apiClient.delete(`/content/radar/${id}`);
      setRadarItems(radarItems.filter(item => item.id !== id));
      toast.success("Removed from radar");
    } catch (error) {
      setRadarItems(radarItems.filter(item => item.id !== id));
      toast.success("Removed from radar");
    }
  };

  const detectPlatform = (url) => {
    if (url.includes("instagram.com")) return "instagram";
    if (url.includes("facebook.com")) return "facebook";
    if (url.includes("tiktok.com")) return "tiktok";
    if (url.includes("twitter.com") || url.includes("x.com")) return "twitter";
    if (url.includes("youtube.com")) return "youtube";
    return "website";
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "instagram": return <Instagram className="w-4 h-4 text-pink-400" />;
      case "facebook": return <Facebook className="w-4 h-4 text-blue-400" />;
      default: return <Globe className="w-4 h-4 text-viraly-orange" />;
    }
  };

  const formatEngagement = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  const toggleNotifications = async (id) => {
    const item = radarItems.find(i => i.id === id);
    const newState = !item?.notifications_enabled;
    
    // Optimistically update UI
    setRadarItems(items => items.map(item => 
      item.id === id 
        ? { ...item, notifications_enabled: newState }
        : item
    ));
    
    try {
      await apiClient.put(`/content/radar/${id}/notifications`, { enabled: newState });
      toast.success(newState 
        ? "You'll be notified when they post" 
        : "Notifications disabled"
      );
    } catch (error) {
      // Revert on error
      setRadarItems(items => items.map(item => 
        item.id === id 
          ? { ...item, notifications_enabled: !newState }
          : item
      ));
      toast.error("Failed to update notification settings");
    }
  };

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platformId)) {
        if (prev.length === 1) return prev;
        return prev.filter(p => p !== platformId);
      }
      return [...prev, platformId];
    });
  };

  // Start creation flow from competitor
  const startCreationFromCompetitor = (item) => {
    setSelectedCompetitor(item);
    setCreationStep(1);
  };

  // Generate content
  const handleGenerate = async () => {
    if (!hasEnoughCredits) {
      toast.error("Not enough credits");
      navigate("/pricing");
      return;
    }

    setLoading(true);
    setCreationStep(5);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return 90;
        return Math.min(90, prev + Math.floor(Math.random() * 8) + 3);
      });
    }, 500);

    try {
      const allPosts = [];
      const daysToGenerate = scheduleType === "bulk" ? days : 1;
      const postsPerDayCount = scheduleType === "bulk" ? postsPerDay : 1;
      const sourceContent = selectedCompetitor?.recent_posts?.[0]?.title || selectedCompetitor?.name || "competitor content";

      for (let day = 0; day < daysToGenerate; day++) {
        for (let postNum = 0; postNum < postsPerDayCount; postNum++) {
          const scheduleDate = new Date();
          scheduleDate.setDate(scheduleDate.getDate() + day + 1);
          scheduleDate.setHours(10 + (postNum * 4), 0, 0, 0);

          const formPayload = new FormData();
          formPayload.append("content_type", "write");
          formPayload.append("platforms", selectedPlatforms.join(","));
          formPayload.append("text", `Create ${contentType === "video" ? "a video script" : "engaging social media content"} inspired by: "${sourceContent}" with a ${selectedTone} tone. Make it unique and original. Day ${day + 1}, variation ${postNum + 1}.`);

          const res = await apiClient.post("/content/generate-ai", formPayload, {
            headers: { "Content-Type": "multipart/form-data" }
          });

          for (const post of res.data.posts) {
            await apiClient.put(`/posts/${post.id}`, {
              scheduled_at: scheduleDate.toISOString(),
              status: "scheduled",
              type: contentType
            });
            allPosts.push(post);
          }
        }
      }

      clearInterval(progressInterval);
      setProgress(100);
      setGeneratedPosts(allPosts);
      await refreshUser();
      setCreationStep(6);
      toast.success(`Created ${allPosts.length} posts!`);

    } catch (error) {
      clearInterval(progressInterval);
      console.error("Generation error:", error);
      toast.error("Failed to generate content");
      setCreationStep(4);
    } finally {
      setLoading(false);
    }
  };

  // If in creation flow, show the creation steps
  if (creationStep > 0) {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-8 max-w-3xl mx-auto">
          {/* Credits Banner */}
          <div className="mb-6 p-4 rounded-xl bg-white/5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-viraly-orange" />
                <span className="text-sm">
                  <span className="text-viraly-orange font-bold">{postsRemaining}</span> post credits
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-blue-400" />
                <span className="text-sm">
                  <span className="text-blue-400 font-bold">{videosRemaining}</span> video credits
                </span>
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-2xl bg-viraly-gradient flex items-center justify-center">
                <Radar className="w-6 h-6 text-white" />
              </div>
              <h1 className="font-heading text-3xl font-bold">Create from Radar</h1>
            </div>
            <p className="text-viraly-muted">
              Creating content inspired by {selectedCompetitor?.name}
            </p>
          </div>

          {/* Progress Steps */}
          {creationStep < 5 && (
            <div className="flex items-center justify-center gap-2 mb-8">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    creationStep >= s ? "bg-viraly-gradient text-white" : "bg-white/10 text-viraly-muted"
                  }`}>
                    {creationStep > s ? <Check className="w-4 h-4" /> : s}
                  </div>
                  {s < 4 && <div className={`w-8 h-0.5 mx-1 ${creationStep > s ? "bg-viraly-orange" : "bg-white/10"}`} />}
                </div>
              ))}
            </div>
          )}

          {/* STEP 1: Content Type (FIRST) */}
          {creationStep === 1 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <button onClick={() => setCreationStep(0)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
                <ArrowLeft className="w-4 h-4" /> Back to Radar
              </button>

              <div className="text-center mb-8">
                <p className="text-viraly-muted">What type of content do you want to create?</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <button
                  onClick={() => { setContentType("post"); setCreationStep(2); }}
                  className="p-6 rounded-xl border-2 border-white/10 hover:border-viraly-orange/50 bg-white/5 transition-all text-center group"
                  data-testid="content-type-post"
                >
                  <div className="w-16 h-16 rounded-xl bg-viraly-orange/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Image className="w-8 h-8 text-viraly-orange" />
                  </div>
                  <h3 className="font-bold text-lg mb-1">Image / Text Post</h3>
                  <p className="text-sm text-viraly-muted">Social media posts</p>
                  <div className="mt-3 flex items-center justify-center gap-2">
                    <span className="text-viraly-orange font-bold">{postsRemaining}</span>
                    <span className="text-xs text-viraly-muted">post credits</span>
                  </div>
                </button>

                <button
                  onClick={() => { setContentType("video"); setCreationStep(2); }}
                  className="p-6 rounded-xl border-2 border-white/10 hover:border-blue-500/50 bg-white/5 transition-all text-center group"
                  data-testid="content-type-video"
                >
                  <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Video className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-lg mb-1">Video Script</h3>
                  <p className="text-sm text-viraly-muted">Video content ideas</p>
                  <div className="mt-3 flex items-center justify-center gap-2">
                    <span className="text-blue-400 font-bold">{videosRemaining}</span>
                    <span className="text-xs text-viraly-muted">video credits</span>
                  </div>
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Single or Bulk (SECOND) */}
          {creationStep === 2 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <button onClick={() => setCreationStep(1)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl font-bold mb-2">How Many?</h2>
                <p className="text-viraly-muted">Create one {contentType === "video" ? "video" : "post"} or schedule many?</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <button
                  onClick={() => { setScheduleType("single"); setCreationStep(3); }}
                  className="card-base p-6 hover:border-viraly-orange/50 transition-all text-left group"
                  data-testid="single-post-btn"
                >
                  <div className="w-12 h-12 rounded-xl bg-viraly-orange/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <FileText className="w-6 h-6 text-viraly-orange" />
                  </div>
                  <h3 className="font-bold mb-1">Single {contentType === "video" ? "Video" : "Post"}</h3>
                  <p className="text-sm text-viraly-muted">Create one inspired {contentType}</p>
                </button>

                <button
                  onClick={() => { setScheduleType("bulk"); setCreationStep(3); }}
                  className="card-base p-6 hover:border-viraly-orange/50 transition-all text-left group"
                  data-testid="bulk-schedule-btn"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Layers className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-bold mb-1">Bulk Schedule</h3>
                  <p className="text-sm text-viraly-muted">Multiple variations over days</p>
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Platforms */}
          {creationStep === 3 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <button onClick={() => setCreationStep(2)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl font-bold mb-2">Select Platforms</h2>
                <p className="text-viraly-muted">Where do you want to post?</p>
              </div>

              <div className="flex gap-3 mb-6">
                {platforms.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => togglePlatform(p.id)}
                    className={`flex-1 p-4 rounded-xl flex flex-col items-center gap-2 transition-all border-2 ${
                      selectedPlatforms.includes(p.id)
                        ? "border-viraly-orange bg-viraly-orange/10"
                        : "border-white/10 bg-white/5 hover:border-white/20"
                    }`}
                    data-testid={`platform-${p.id}`}
                  >
                    <Globe className="w-6 h-6" style={{ color: selectedPlatforms.includes(p.id) ? p.color : "#888" }} />
                    <span className="font-medium">{p.name}</span>
                    {selectedPlatforms.includes(p.id) && <Check className="w-4 h-4 text-viraly-orange" />}
                  </button>
                ))}
              </div>

              {/* Bulk options */}
              {scheduleType === "bulk" && (
                <div className="mb-6 p-4 rounded-xl bg-white/5 space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium">Duration</label>
                      <span className="text-viraly-orange font-bold">{days} days</span>
                    </div>
                    <Slider value={[days]} onValueChange={(val) => setDays(val[0])} min={1} max={maxDays} step={1} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Posts per day per platform</label>
                    <div className="flex gap-2">
                      {[1, 2, 3].map((n) => (
                        <button
                          key={n}
                          onClick={() => setPostsPerDay(n)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                            postsPerDay === n ? "bg-viraly-orange text-white" : "bg-white/10 hover:bg-white/20"
                          }`}
                        >
                          {n}x
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Credit Warning */}
              <div className={`mb-6 p-4 rounded-xl border ${
                isVideoContent ? "bg-blue-500/10 border-blue-500/20" : "bg-viraly-orange/10 border-viraly-orange/20"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isVideoContent ? (
                      <Video className="w-5 h-5 text-blue-400" />
                    ) : (
                      <FileText className="w-5 h-5 text-viraly-orange" />
                    )}
                    <div>
                      <p className="text-sm font-medium">This will use {creditsNeeded} {creditType} credit{creditsNeeded !== 1 ? 's' : ''}</p>
                      <p className="text-xs text-viraly-muted">
                        {scheduleType === "bulk" 
                          ? `${days} days x ${postsPerDay}/day x ${selectedPlatforms.length} platforms`
                          : `${selectedPlatforms.length} platform(s) selected`}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    hasEnoughCredits ? 'bg-viraly-success/20 text-viraly-success' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {creditsAvailable} {creditType} credits
                  </span>
                </div>
              </div>

              <Button onClick={() => setCreationStep(4)} disabled={selectedPlatforms.length === 0} className="btn-primary w-full py-4">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          )}

          {/* STEP 4: Tone Selection */}
          {creationStep === 4 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <button onClick={() => setCreationStep(3)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl font-bold mb-2">Select Tone</h2>
                <p className="text-viraly-muted">How should your content sound?</p>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-8">
                {tones.map((tone) => (
                  <button
                    key={tone.id}
                    onClick={() => setSelectedTone(tone.id)}
                    className={`p-4 rounded-xl flex items-center gap-3 transition-all border-2 ${
                      selectedTone === tone.id
                        ? "border-viraly-orange bg-viraly-orange/10"
                        : "border-white/10 bg-white/5 hover:border-white/20"
                    }`}
                  >
                    <span className="text-2xl">
                      {tone.id === "professional" && "briefcase"}
                      {tone.id === "casual" && "smile"}
                      {tone.id === "humorous" && "laugh"}
                      {tone.id === "inspirational" && "star"}
                    </span>
                    <span className="font-medium">{tone.label}</span>
                    {selectedTone === tone.id && <Check className="w-4 h-4 ml-auto text-viraly-orange" />}
                  </button>
                ))}
              </div>

              <Button
                onClick={handleGenerate}
                disabled={!hasEnoughCredits || loading}
                className="btn-primary w-full py-4"
                data-testid="generate-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-5 h-5 mr-2" /> Generate ({creditsNeeded} {creditType} credit{creditsNeeded !== 1 ? 's' : ''})</>
                )}
              </Button>
            </motion.div>
          )}

          {/* STEP 5: Generating */}
          {creationStep === 5 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-viraly-gradient flex items-center justify-center mx-auto mb-6 animate-pulse">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-2">Creating Inspired Content</h2>
              <p className="text-viraly-muted mb-6">
                Generating unique posts inspired by {selectedCompetitor?.name}...
              </p>
              <div className="w-full max-w-xs mx-auto bg-white/10 rounded-full h-3 mb-2">
                <div className="bg-viraly-gradient h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-sm text-viraly-orange">{Math.round(progress)}%</p>
              <ProgressMessages progress={progress} />
            </motion.div>
          )}

          {/* STEP 6: Done */}
          {creationStep === 6 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-viraly-success/20 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-viraly-success" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-2">Content Created!</h2>
                <p className="text-viraly-muted">{generatedPosts.length} posts scheduled successfully</p>
              </div>

              {/* Preview */}
              <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
                {generatedPosts.slice(0, 3).map((post, i) => (
                  <div key={i} className="card-base p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        post.platform === "facebook" ? "bg-blue-500/20" :
                        post.platform === "instagram" ? "bg-pink-500/20" : "bg-black/40"
                      }`}>
                        <Globe className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium capitalize">{post.platform}</p>
                        <p className="text-xs text-viraly-muted line-clamp-1">{post.caption?.substring(0, 60)}...</p>
                      </div>
                    </div>
                  </div>
                ))}
                {generatedPosts.length > 3 && (
                  <p className="text-center text-sm text-viraly-muted">+{generatedPosts.length - 3} more</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Button onClick={() => navigate("/posts?tab=scheduled")} className="btn-primary flex items-center justify-center gap-2">
                  <Eye className="w-4 h-4" /> View in Posts
                </Button>
                <Button onClick={() => navigate("/calendar")} className="btn-secondary flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4" /> View Calendar
                </Button>
              </div>

              <Button
                onClick={() => { setCreationStep(0); setGeneratedPosts([]); setSelectedCompetitor(null); }}
                className="btn-secondary w-full"
              >
                <Radar className="w-4 h-4 mr-2" /> Back to Radar
              </Button>
            </motion.div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Main Radar View
  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
              <Radar className="w-8 h-8 text-viraly-orange" />
              Content Radar
            </h1>
            <p className="text-viraly-muted mt-2">
              Monitor competitors and trending content in your niche
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              className="btn-secondary flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => setShowAddModal(true)}
              disabled={radarItems.length >= maxUrls}
              className="btn-primary flex items-center gap-2"
              data-testid="add-competitor-btn"
            >
              <Plus className="w-4 h-4" />
              Add Competitor
            </Button>
          </div>
        </div>

        {/* Usage indicator */}
        <div className="mb-6 p-4 rounded-xl bg-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-viraly-orange" />
            <span className="text-sm">
              Monitoring <span className="text-viraly-orange font-bold">{radarItems.length}</span> of{" "}
              <span className="font-bold">{maxUrls}</span> URLs
            </span>
          </div>
          {radarItems.length >= maxUrls && (
            <span className="text-xs text-yellow-400 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Upgrade to monitor more
            </span>
          )}
        </div>

        {/* Radar Items Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
          </div>
        ) : radarItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <Radar className="w-16 h-16 text-viraly-muted mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No competitors tracked yet</h3>
            <p className="text-viraly-muted mb-6">
              Add URLs of competitors or influencers you want to monitor
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Competitor
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {radarItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card-base p-6"
                data-testid={`radar-item-${item.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getPlatformIcon(item.platform)}
                    <div>
                      <h3 className="font-bold text-lg">{item.name}</h3>
                      <a 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-viraly-muted hover:text-viraly-orange flex items-center gap-1"
                      >
                        {item.url.replace(/https?:\/\//, '').substring(0, 30)}...
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(item.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg text-viraly-muted hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-white/5 text-center">
                    <p className="text-xs text-viraly-muted">Posts Today</p>
                    <p className="text-xl font-bold text-viraly-orange">{item.metrics?.posts_today || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5 text-center">
                    <p className="text-xs text-viraly-muted">Avg Engagement</p>
                    <p className="text-xl font-bold text-viraly-success">
                      {formatEngagement(item.metrics?.avg_engagement || 0)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5 text-center">
                    <p className="text-xs text-viraly-muted" title="When we last analyzed this competitor's page">
                      Last Analyzed
                    </p>
                    <p className="text-sm font-medium">
                      {item.last_checked ? new Date(item.last_checked).toLocaleDateString() : "Pending..."}
                    </p>
                    {item.last_post_date && (
                      <p className="text-xs text-viraly-success mt-1">
                        Latest post: {new Date(item.last_post_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>

                {/* Multiple URLs Section */}
                {item.urls && item.urls.length > 1 && (
                  <div className="mb-4 p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-viraly-muted mb-2">Connected Profiles</p>
                    <div className="flex flex-wrap gap-2">
                      {item.urls.map((urlObj, i) => (
                        <a
                          key={i}
                          href={urlObj.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        >
                          {getPlatformIcon(urlObj.platform)}
                          <span className="text-xs">{urlObj.platform}</span>
                          <ExternalLink className="w-3 h-3 text-viraly-muted" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trending Topics */}
                {item.metrics?.trending_topics?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-viraly-muted mb-2 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> Trending Topics
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {item.metrics.trending_topics.map((topic, i) => (
                        <span key={i} className="px-2 py-1 rounded-full bg-viraly-orange/20 text-viraly-orange text-xs">
                          #{topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Posts */}
                {item.recent_posts?.length > 0 && (
                  <div>
                    <p className="text-xs text-viraly-muted mb-2">Recent Posts</p>
                    <div className="space-y-2">
                      {item.recent_posts.slice(0, 2).map((post, i) => (
                        <div key={i} className="p-3 rounded-lg bg-white/5 flex items-center justify-between">
                          <span className="text-sm truncate flex-1 mr-3">{post.title}</span>
                          <div className="flex items-center gap-3 text-xs text-viraly-muted">
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" />
                              {formatEngagement(post.engagement)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.time}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Create Similar Content Button */}
                <Button
                  className="w-full mt-4 btn-primary flex items-center justify-center gap-2"
                  onClick={() => startCreationFromCompetitor(item)}
                  data-testid={`create-similar-${item.id}`}
                >
                  <Share2 className="w-4 h-4" />
                  Create Similar Content
                </Button>
                
                {/* Notification toggle */}
                <div className="mt-3 flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <span className="text-xs text-viraly-muted">Get notified when they post</span>
                  <button 
                    className={`w-10 h-5 rounded-full transition-colors ${item.notifications_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}
                    onClick={() => toggleNotifications(item.id)}
                    data-testid={`notification-toggle-${item.id}`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${item.notifications_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Add Competitor Modal */}
        <AnimatePresence>
          {showAddModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowAddModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-md p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold">Add Competitor</h2>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 hover:bg-white/10 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Name</label>
                    <Input
                      placeholder="e.g., Gary Vee, TechCrunch"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="input-base"
                      data-testid="competitor-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">URL</label>
                    <Input
                      placeholder="https://instagram.com/garyvee"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      className="input-base"
                      data-testid="competitor-url-input"
                    />
                    <p className="text-xs text-viraly-muted mt-2">
                      Supports: Instagram, Facebook, TikTok, Twitter, YouTube, and websites
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => setShowAddModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddCompetitor}
                    disabled={adding || !newUrl.trim() || !newName.trim()}
                    className="btn-primary flex-1"
                    data-testid="confirm-add-competitor"
                  >
                    {adding ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      "Add to Radar"
                    )}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
